import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ROLE_CATEGORIES } from "@/pages/RegisterStaff";
import { ADMIN } from "@/constants/testIds";
import { StatusPill, PageHero } from "@/components/shared/Visual";
import { Plus, Briefcase, Trash2, CheckCircle2, Mail, Lock, MessageSquare, AlertTriangle, FileText } from "lucide-react";

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [resubmitFor, setResubmitFor] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    title: "", location: "", date: "", start_time: "", end_time: "", rate_of_pay: 20,
    required_role: ROLE_CATEGORIES[0], workers_needed: 1, hours: 8,
    country_scope: "IE", required_licence: "ANY", other_requirements: "",
    dress_code: "", site_instructions: "", call_schedule: "", sops: "",
    agreement_terms: "", contact_instructions: "",
  });

  const load = async () => {
    try { const { data } = await api.get("/jobs"); setJobs(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const post = async () => {
    try {
      await api.post("/jobs", { ...f, rate_of_pay: Number(f.rate_of_pay), workers_needed: Number(f.workers_needed), hours: Number(f.hours) });
      toast.success("Job posted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeJob = async (id) => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try { await api.delete(`/jobs/${id}`); toast.success("Job deleted"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approve = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/jobs/${id}/approve`); toast.success("Job approved & published"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendResubmit = async () => {
    if (!resubmitFor || !note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/jobs/${resubmitFor.id}/resubmit-request`, { message: note.trim() });
      toast.success("Sent back to company with your note");
      setResubmitFor(null); setNote(""); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const pending = jobs.filter((j) => j.status === "pending_review" || j.status === "resubmit_requested");
  const live = jobs.filter((j) => j.status === "open");
  const other = jobs.filter((j) => !["pending_review", "resubmit_requested", "open"].includes(j.status));

  return (
    <div className="space-y-6" data-testid="admin-jobs-page">
      <PageHero
        eyebrow="Jobs Board"
        title="Job Oversight"
        sub={`${pending.length} awaiting review · ${live.length} live`}
        right={
          <Dialog>
            <DialogTrigger asChild>
              <Button className="btn-brand" data-testid={ADMIN.postJob}><Plus className="w-4 h-4 mr-1" /> Post Job</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle>Post a Job (admin)</DialogTitle></DialogHeader>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="job-title" /></div>
                <div><Label>Location</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="job-location" /></div>
                <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
                <div><Label>Rate €/hr</Label><Input type="number" value={f.rate_of_pay} onChange={(e) => setF({ ...f, rate_of_pay: e.target.value })} /></div>
                <div><Label>Start time</Label><Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
                <div><Label>End time</Label><Input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
                <div><Label>Workers needed</Label><Input type="number" min={1} value={f.workers_needed} onChange={(e) => setF({ ...f, workers_needed: e.target.value })} /></div>
                <div><Label>Hours</Label><Input type="number" step="0.5" min={1} value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} /></div>
                <div className="md:col-span-2">
                  <Label>Required role</Label>
                  <Select value={f.required_role} onValueChange={(v) => setF({ ...f, required_role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLE_CATEGORIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country scope</Label>
                  <Select value={f.country_scope} onValueChange={(v) => setF({ ...f, country_scope: v })}>
                    <SelectTrigger data-testid="job-country-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="IE">Ireland only</SelectItem>
                      <SelectItem value="UK">United Kingdom only</SelectItem>
                      <SelectItem value="BOTH">Ireland or United Kingdom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Required licence</Label>
                  <Select value={f.required_licence} onValueChange={(v) => setF({ ...f, required_licence: v })}>
                    <SelectTrigger data-testid="job-required-licence"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ANY">Any (PSA or SIA)</SelectItem>
                      <SelectItem value="PSA">PSA (Ireland)</SelectItem>
                      <SelectItem value="SIA">SIA (UK)</SelectItem>
                      <SelectItem value="NONE">None required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2"><Label>Other mandatory requirements</Label><Textarea rows={2} value={f.other_requirements} onChange={(e) => setF({ ...f, other_requirements: e.target.value })} data-testid="job-other-reqs" /></div>
                <div className="md:col-span-2"><Label>Dress code</Label><Input value={f.dress_code} onChange={(e) => setF({ ...f, dress_code: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Site instructions</Label><Textarea rows={2} value={f.site_instructions} onChange={(e) => setF({ ...f, site_instructions: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>SOPs</Label><Textarea rows={2} value={f.sops} onChange={(e) => setF({ ...f, sops: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Call schedule</Label><Textarea rows={2} value={f.call_schedule} onChange={(e) => setF({ ...f, call_schedule: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Agreement terms</Label><Textarea rows={2} value={f.agreement_terms} onChange={(e) => setF({ ...f, agreement_terms: e.target.value })} /></div>
                <div className="md:col-span-2"><Label>Contact / reporting instructions</Label><Textarea rows={2} value={f.contact_instructions} onChange={(e) => setF({ ...f, contact_instructions: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={post} className="btn-brand" data-testid="job-post-submit">Post Job</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Pending review queue */}
      {pending.length > 0 && (
        <div className="space-y-3" data-testid="admin-jobs-pending">
          <div className="eyebrow eyebrow-brand">Awaiting review</div>
          {pending.map((j) => {
            const tier = (j.service_tier === "assisted_match" ? "tracked_deployment" : (j.service_tier || "platform_match"));
            const tierPct = tier === "elite_protection" ? 10 : tier === "tracked_deployment" ? 5 : 3;
            const tierLabel = tierPct === 10 ? "Elite Protection" : tierPct === 5 ? "Tracked Deployment" : "Platform Match";
            const bookingValue = Number(j.rate_of_pay || 0) * Number(j.workers_needed || 1) * Number(j.hours || 0);
            const fee = bookingValue * (tierPct / 100);
            return (
              <div key={j.id} className="card-dark p-5 border-l-4 border-l-amber-400" data-testid={`admin-job-${j.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-outfit font-semibold txt-hi">{j.title}</span>
                      <StatusPill kind="standby">{j.status === "resubmit_requested" ? "Awaiting Resubmit" : "Pending Review"}</StatusPill>
                      <span className={`spill spill-${tierPct === 10 ? "managed" : tierPct === 5 ? "tracked" : "match"}`}>
                        <span className="dot" /> {tierPct}% · {tierLabel}
                      </span>
                    </div>
                    <div className="eyebrow mt-2">{j.location} · {j.date} · {j.required_role} · {j.workers_needed}× · {j.hours}h</div>

                    {/* Pricing structure — visible to admin so they can adjudicate rate compliance */}
                    <div className="mt-3 grid gap-2 sm:grid-cols-3">
                      <MiniStat label="Advertised rate" value={`€${Number(j.rate_of_pay).toFixed(2)}/hr`} />
                      <MiniStat label="Booking value" value={`€${bookingValue.toFixed(2)}`} />
                      <MiniStat label={`SecuSource fee (${tierPct}%)`} value={`€${fee.toFixed(2)}`} accent />
                    </div>

                    {j.resubmit_note && (
                      <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 text-xs font-manrope p-2">
                        <span className="uppercase tracking-widest font-outfit font-bold mr-1">Sent back:</span>{j.resubmit_note}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 flex-shrink-0">
                    <Button size="sm" onClick={() => approve(j.id)} disabled={busy} className="btn-brand" data-testid={`admin-approve-job-${j.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve &amp; Publish
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setResubmitFor(j); setNote(j.resubmit_note || ""); }} className="btn-ghost-dark" data-testid={`admin-resubmit-btn-${j.id}`}>
                      <Mail className="w-3.5 h-3.5 mr-1" /> Send back with note
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => removeJob(j.id)} className="btn-ghost-dark hover:text-rose-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Live jobs */}
      {live.length > 0 && (
        <div className="space-y-3">
          <div className="eyebrow eyebrow-brand">Live</div>
          {live.map((j) => (
            <div key={j.id} className="card-dark p-5 border-l-4 border-l-brand" data-testid={`admin-job-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-outfit font-semibold txt-hi">{j.title}</span>
                    <StatusPill kind="live">Open</StatusPill>
                  </div>
                  <div className="eyebrow mt-2">{j.location} · {j.date} · €{j.rate_of_pay}/hr · {j.required_role}</div>
                  <div className="text-xs txt-lo font-manrope mt-1">
                    {(j.country_scope || "IE") === "BOTH" ? "IE / UK" : (j.country_scope || "IE")}
                    {" · "}{j.required_licence || "ANY"} licence · Applicants: {(j.applicants || []).length}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Link to={`/admin/jobs/${j.id}`} data-testid={`admin-open-job-${j.id}`}>
                    <Button variant="outline" size="sm" className="btn-ghost-dark">
                      <FileText className="w-3.5 h-3.5 mr-1" /> Details
                    </Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={() => removeJob(j.id)} className="btn-ghost-dark hover:text-rose-400" data-testid={`admin-delete-job-${j.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {other.length > 0 && (
        <div className="space-y-3">
          <div className="eyebrow eyebrow-brand">Other</div>
          {other.map((j) => (
            <div key={j.id} className="card-dark p-4 opacity-70" data-testid={`admin-job-${j.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-outfit font-semibold txt-hi text-sm">{j.title}</span>
                  <span className="ml-2 spill spill-neutral"><span className="dot" /> {j.status}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {jobs.length === 0 && (
        <div className="card-dark p-10 text-center">
          <Briefcase className="w-10 h-10 txt-lo mx-auto mb-3" />
          <div className="font-outfit font-semibold txt-hi">No jobs yet</div>
          <div className="txt-lo text-sm font-manrope">Waiting for companies to post.</div>
        </div>
      )}

      {/* Resubmit dialog */}
      <Dialog open={!!resubmitFor} onOpenChange={(o) => !o && setResubmitFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-brand" /> Send back for resubmission
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-white/70 font-manrope">
              The company will receive an email with your note and will be prompted to edit &amp; resubmit the job.
            </div>
            <div>
              <Label>Note to company <span className="text-rose-500">*</span></Label>
              <Textarea
                rows={5}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Rate is below ERO minimum for this role. Please increase to at least €14/hr and resubmit."
                data-testid="admin-resubmit-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResubmitFor(null)}>Cancel</Button>
            <Button onClick={sendResubmit} disabled={busy || !note.trim()} className="bg-brand hover:bg-brand-hover text-white" data-testid="admin-resubmit-send">
              <Mail className="w-4 h-4 mr-1" /> Send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return (
    <div className={`card-inner p-2.5 ${accent ? "border-brand/40" : ""}`}>
      <div className="eyebrow">{label}</div>
      <div className={`font-outfit font-bold text-base mt-1 ${accent ? "text-brand" : "txt-hi"}`}>{value}</div>
    </div>
  );
}
